import { useEffect, useRef, useState } from 'react'
import { listSchemes } from '../api/schemes'
import type { ColorScheme } from '../api/schemes'
import type { ColorTheme } from './Entry/colorTheme'
import { schemeToTheme, findMatchingScheme } from './schemeUtils'
import { PALETTES, paletteToTheme, findMatchingPalette } from '../lib/palettes'

interface Props {
  colorTheme: ColorTheme | null
  onColorThemeChange: (v: ColorTheme | null) => void
  username: string | null
}

function Swatches({ colors }: { colors: ColorTheme }) {
  return (
    <span style={{ display: 'inline-flex', gap: 2, verticalAlign: 'middle', flexShrink: 0 }}>
      {[colors.bg, colors.fg, colors.color1, colors.color2].map((c, i) => (
        <span
          key={i}
          style={{ width: 10, height: 10, background: c, display: 'inline-block', border: '1px solid var(--color-fg)', opacity: 0.85 }}
        />
      ))}
    </span>
  )
}

interface OptionItem {
  value: string
  label: string
  colors: ColorTheme | null
  disabled?: boolean
  group?: string
}

export function SchemePicker({ colorTheme, onColorThemeChange, username }: Props) {
  const [schemes, setSchemes] = useState<ColorScheme[]>([])
  const [selected, setSelected] = useState<string>(() => colorTheme ? 'custom' : 'none')
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!username) return
    listSchemes(username).then((list) => {
      setSchemes(list)
      const schemeId = findMatchingScheme(colorTheme, list)
      const builtinId = schemeId ? null : findMatchingPalette(colorTheme)
      setSelected(schemeId ?? builtinId ?? (colorTheme ? 'custom' : 'none'))
    }).catch(() => {})
  }, [username]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return
    function handler(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  function handleSelect(value: string) {
    setSelected(value)
    setOpen(false)
    if (value === 'none') {
      onColorThemeChange(null)
    } else if (value.startsWith('builtin:')) {
      const id = value.slice(8)
      const palette = PALETTES.find((p) => p.id === id)
      if (palette) onColorThemeChange(paletteToTheme(palette))
    } else {
      const scheme = schemes.find((s) => s.id === value)
      if (scheme) onColorThemeChange(schemeToTheme(scheme))
    }
  }

  if (!username) return null

  const options: OptionItem[] = [
    { value: 'none', label: '-- no theme --', colors: null },
    ...PALETTES.map((p) => ({ value: `builtin:${p.id}`, label: p.name, colors: paletteToTheme(p), group: 'built-in' })),
    ...schemes.map((s) => ({ value: s.id, label: s.name, colors: schemeToTheme(s), group: 'saved' })),
    ...(selected === 'custom' ? [{ value: 'custom', label: '-- custom --', colors: colorTheme, disabled: true }] : []),
  ]

  const current = options.find((o) => o.value === selected)

  const groups = [
    { key: 'none', items: options.filter((o) => !o.group && o.value !== 'custom') },
    { key: 'built-in', items: options.filter((o) => o.group === 'built-in') },
    { key: 'saved', items: options.filter((o) => o.group === 'saved') },
    { key: 'custom', items: options.filter((o) => o.value === 'custom') },
  ].filter((g) => g.items.length > 0)

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="color scheme"
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.3rem',
          background: 'transparent',
          color: 'var(--color-fg)',
          fontSize: '0.8rem',
          width: 'auto',
          padding: '0.15rem 0.3rem',
          border: '1px solid var(--color-fg)',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        <span style={{ visibility: current?.colors ? 'visible' : 'hidden' }}>
          {current?.colors ? <Swatches colors={current.colors} /> : <Swatches colors={{ bg: '#000', fg: '#000', color1: '#000', color2: '#000' }} />}
        </span>
        <span style={{ width: '7rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>{current?.label ?? '-- no theme --'}</span>
        <span style={{ opacity: 0.6, fontSize: '0.65rem' }}>▾</span>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="color scheme"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 100,
            background: 'var(--color-bg)',
            border: '1px solid var(--color-fg)',
            minWidth: '12rem',
            maxHeight: '16rem',
            overflowY: 'auto',
            marginTop: 2,
          }}
        >
          {groups.map((group, gi) => (
            <div key={group.key}>
              {group.key !== 'none' && group.key !== 'custom' && (
                <div style={{ padding: '0.2rem 0.5rem', fontSize: '0.7rem', opacity: 0.5, borderTop: gi > 0 ? '1px solid var(--color-fg)' : undefined }}>
                  {group.key}
                </div>
              )}
              {group.items.map((opt) => (
                <div
                  key={opt.value}
                  role="option"
                  aria-selected={opt.value === selected}
                  aria-disabled={opt.disabled}
                  onClick={() => !opt.disabled && handleSelect(opt.value)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    padding: '0.3rem 0.5rem',
                    fontSize: '0.8rem',
                    cursor: opt.disabled ? 'default' : 'pointer',
                    opacity: opt.disabled ? 0.45 : 1,
                    background: opt.value === selected ? 'var(--color-fg)' : 'transparent',
                    color: opt.value === selected ? 'var(--color-bg)' : 'var(--color-fg)',
                  }}
                  onMouseEnter={(e) => {
                    if (!opt.disabled && opt.value !== selected) {
                      (e.currentTarget as HTMLElement).style.opacity = '0.75'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!opt.disabled && opt.value !== selected) {
                      (e.currentTarget as HTMLElement).style.opacity = '1'
                    }
                  }}
                >
                  {opt.colors
                    ? <Swatches colors={opt.colors} />
                    : <span style={{ width: 46, display: 'inline-block' }} />
                  }
                  <span>{opt.label}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
